// src/components/admin/TenantManagement.js - Dedicated tenant management component
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../../contexts/AuthContext';
import { useApiCall } from '../../hooks/useApiCall';
import apiService from '../../services/apiService';
import './Admin.css';
import TenantUserManagement from './TenantUserManagement';
import { TenantFeatureManagement } from './TenantFeatureManagement';
import InviteLinksModal from './InviteLinksModal';

const TenantManagement = () => {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [showAddTenantModal, setShowAddTenantModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null); // For managing users
  const [selectedConfigTenant, setSelectedConfigTenant] = useState(null); // For feature config modal
  const [issuedLinks, setIssuedLinks] = useState(null); // { title, intro, links, filename } after the first Admin's invite
  const [tenantPagination, setTenantPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0,
    hasMore: false
  });
  
  const { loading, error, makeRequest } = useApiCall();

  useEffect(() => {
    // Load tenants when component mounts
    loadTenants();
  }, []);

  const loadTenants = async (page = 1, status = 'all') => {
    try {
      const data = await makeRequest(
        () => apiService.get(`/api/v1/tenants?page=${page}&limit=50&status=${status}`, user),
        'Loading tenants...'
      );

      if (data.success && data.data) {
        setTenants(data.data.tenants || []);
        setTenantPagination(data.data.pagination || {
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0,
          hasMore: false
        });
      } else {
        console.error('Unexpected tenant data structure:', data);
        setTenants([]);
      }
    } catch (err) {
      console.error('Error loading tenants:', err);
      // Error is handled by the useApiCall hook
    }
  };

  const createTenant = async (tenantData) => {
    try {
      const data = await makeRequest(
        () => apiService.post('/api/v1/tenants', tenantData, user),
        'Creating tenant...'
      );

      if (data.success) {
        const newTenant = data.data;

        // Automatically invite the admin user with Admin and User roles
        if (tenantData.adminEmail) {
          try {
            const inviteData = {
              email: tenantData.adminEmail,
              roles: ['Admin', 'User'] // Hard-coded roles as requested
            };
            
            const inviteResult = await makeRequest(
              () => apiService.post(`/api/v1/tenants/${newTenant.id}/users/invite`, inviteData, user),
              'Sending admin invitation...'
            );

            if (inviteResult?.success) {
              const invited = inviteResult.data;
              setIssuedLinks({
                title: 'Admin invitation created',
                intro: invited.emailSent
                  ? `An invitation email was sent to ${invited.email}. If it doesn't arrive, share this link another way.`
                  : `The invitation email to ${invited.email} could not be sent. Share this link another way.`,
                links: [{ email: invited.email, inviteLink: invited.inviteLink }],
                filename: `${newTenant.slug || 'tenant'}-invite-links`
              });
            }

          } catch (inviteErr) {
            console.error('Error sending admin invitation:', inviteErr);
            // Don't fail the whole process if invitation fails
            // The tenant was created successfully
            alert('Tenant created successfully, but there was an error sending the admin invitation. You can invite the user manually from User Management.');
          }
        }
        
        // Close modal and refresh tenant list
        setShowAddTenantModal(false);
        await loadTenants();
        return newTenant;
      } else {
        throw new Error(data.error || 'Failed to create tenant');
      }
    } catch (err) {
      console.error('Error creating tenant:', err);
      throw err;
    }
  };

  return (
    <div className="admin-page">
      {/* If a tenant is selected, show user management for that tenant */}
      {selectedTenant ? (
        <TenantUserManagement 
          tenant={selectedTenant}
          onBack={() => setSelectedTenant(null)}
        />
      ) : (
        /* Otherwise show tenant list */
        <>
          <div className="flex flex-between mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">Tenant Management</h2>
            <div className="flex gap-2">
              <button 
                onClick={() => loadTenants()}
                className="btn btn-secondary"
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
              <button 
                onClick={() => setShowAddTenantModal(true)}
                className="btn btn-primary"
                disabled={loading}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Tenant
              </button>
            </div>
          </div>

          {/* Pagination Info */}
          {tenantPagination.total > 0 && (
            <div className="mb-4 text-sm text-gray-600">
              Showing {tenants.length} of {tenantPagination.total} tenants
            </div>
          )}

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-8">
              <div className="loading-dots mx-auto mb-3">
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
                <div className="loading-dot"></div>
              </div>
              <p className="text-gray-600">Loading tenants...</p>
            </div>
          ) : error ? (
            /* Error State */
            <div className="alert alert-error">
              <div>
                <strong>Error loading tenants:</strong> {error}
                <br />
                <small>Check the browser console for more details.</small>
              </div>
              <button 
                onClick={() => loadTenants()}
                className="btn btn-secondary btn-sm ml-3"
              >
                Try Again
              </button>
            </div>
          ) : tenants.length === 0 ? (
            /* Empty State */
            <div className="card text-center py-12">
              <div className="icon-container gray">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">
                No tenants found
              </h3>
              <p className="text-gray-500 mb-4">
                Get started by creating your first tenant organization.
              </p>
              <button 
                onClick={() => setShowAddTenantModal(true)}
                className="btn btn-primary"
              >
                Create First Tenant
              </button>
            </div>
          ) : (
            /* Tenant List */
            <div className="space-y-4">
              {tenants.map(tenant => (
                <TenantCard
                  key={tenant.id}
                  tenant={tenant}
                  onManageUsers={() => setSelectedTenant(tenant)}
                  onConfigureTenant={() => setSelectedConfigTenant(tenant)}
                />
              ))}
            </div>
          )}

          {/* Tenant Feature Config Modal */}
          {selectedConfigTenant && (
            <TenantFeatureManagement
              tenant={selectedConfigTenant}
              onClose={() => setSelectedConfigTenant(null)}
            />
          )}

          {/* Add Tenant Modal */}
          {showAddTenantModal && (
            <AddTenantModal 
              onClose={() => setShowAddTenantModal(false)} 
              onSubmit={createTenant}
              loading={loading}
            />
          )}
        </>
      )}

      {/* The new tenant's first Admin invite link */}
      {issuedLinks && (
        <InviteLinksModal
          title={issuedLinks.title}
          intro={issuedLinks.intro}
          links={issuedLinks.links}
          filename={issuedLinks.filename}
          onClose={() => setIssuedLinks(null)}
        />
      )}
    </div>
  );
};

// Tenant Card Component
const TenantCard = ({ tenant, onManageUsers, onConfigureTenant }) => {
  return (
    <div className="tenant-card">
      <div className="flex flex-between">
        <div className="flex-1">
          <div className="flex gap-3" style={{ alignItems: 'center' }}>
            <h3 className="text-lg font-semibold text-gray-800">
              {tenant.name}
            </h3>
            <span className={`status-pill ${tenant.status === 'active' ? 'active' : 'archived'}`}>
              {tenant.status}
            </span>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Slug: {tenant.slug}
          </p>
          <div className="flex gap-4 mt-2">
            <p className="text-sm text-gray-500">
              {tenant.userCount || tenant.activeUserCount || 0} users
            </p>
            {tenant.createdAt && (
              <p className="text-sm text-gray-500">
                Created: {new Date(tenant.createdAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
        <div className="tenant-actions">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => onConfigureTenant(tenant)}
          >
            Tenant Config
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => onManageUsers(tenant)}
          >
            Manage Users
          </button>
        </div>
      </div>
    </div>
  );
};

TenantCard.propTypes = {
  tenant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    userCount: PropTypes.number,
    activeUserCount: PropTypes.number,
    createdAt: PropTypes.string
  }).isRequired,
  onManageUsers: PropTypes.func.isRequired,
  onConfigureTenant: PropTypes.func.isRequired
};

// Add Tenant Modal Component
const AddTenantModal = ({ onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    adminEmail: '',
    settings: {}
  });
  const [formErrors, setFormErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Auto-generate slug from name
    if (name === 'name' && !formData.slug) {
      const autoSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
        .replace(/\s+/g, '-') // Replace spaces with hyphens
        .replace(/-+/g, '-') // Replace multiple hyphens with single
        .trim();
      setFormData(prev => ({
        ...prev,
        slug: autoSlug
      }));
    }

    // Clear errors for this field
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'Organization name is required';
    }

    if (!formData.slug.trim()) {
      errors.slug = 'Slug is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens';
    }

    if (!formData.adminEmail.trim()) {
      errors.adminEmail = 'Admin email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.adminEmail)) {
      errors.adminEmail = 'Please enter a valid email address';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        adminEmail: formData.adminEmail.trim(), // Always include since it's required
        settings: formData.settings
      };

      await onSubmit(submitData);
    } catch (err) {
      console.error('Error submitting form:', err);
      // Error handling is done in the parent component
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Add New Tenant</h3>
          <button 
            onClick={onClose} 
            className="text-gray-400 hover:text-gray-600"
            disabled={loading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="form-label" htmlFor="name">
              Organization Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
              placeholder="e.g., Acme Corporation"
              disabled={loading}
              required
            />
            {formErrors.name && (
              <p className="text-sm text-red-600 mt-1">{formErrors.name}</p>
            )}
          </div>

          <div className="mb-4">
            <label className="form-label" htmlFor="slug">
              Slug *
            </label>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleInputChange}
              className={`form-input ${formErrors.slug ? 'border-red-500' : ''}`}
              placeholder="e.g., acme-corp"
              disabled={loading}
              required
            />
            {formErrors.slug && (
              <p className="text-sm text-red-600 mt-1">{formErrors.slug}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              URL-friendly identifier (lowercase letters, numbers, and hyphens only)
            </p>
          </div>

          <div className="mb-6">
            <label className="form-label" htmlFor="adminEmail">
              Admin Email *
            </label>
            <input
              type="email"
              id="adminEmail"
              name="adminEmail"
              value={formData.adminEmail}
              onChange={handleInputChange}
              className={`form-input ${formErrors.adminEmail ? 'border-red-500' : ''}`}
              placeholder="admin@company.com"
              disabled={loading}
              required
            />
            {formErrors.adminEmail && (
              <p className="text-sm text-red-600 mt-1">{formErrors.adminEmail}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Email address for the tenant administrator (will receive an invitation)
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
              disabled={loading || !formData.name.trim() || !formData.slug.trim() || !formData.adminEmail.trim()}
            >
              {loading ? (
                <>
                  <div className="loading-dots mr-2">
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                    <div className="loading-dot"></div>
                  </div>
                  Creating...
                </>
              ) : (
                'Create Tenant'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

AddTenantModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired
};

export default TenantManagement;